<script setup lang="ts">
import type { GetOnePriceOverrideRevertResponse, PriceOverrideDB } from "@classes-service-types/index";
import { PriceOverrideStatus } from "@classes-service-types/index";
import { Libraries } from "@classes-service-types/libraries";
import { datadogLogs } from "@datadog/browser-logs";
import { useQueryClient } from "@tanstack/vue-query";
import useVuelidate from "@vuelidate/core";
import { helpers, maxLength } from "@vuelidate/validators";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import type { ComputedRef, PropType } from "vue";
import { computed, reactive, ref, toRef, toRefs, unref, watch } from "vue";
import { useToast } from "vue-toast-notification";
import { uuid } from "vue-uuid";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import { ERROR_MESSAGE } from "@/components/StudentFulfillment/components/modals/HousingFulfillmentModal/constants";
import {
  buildStatusRow,
  canUpdatePriceOverride,
  findLatestPrice,
  generateDefaultPriceOverride,
  generatePriceOverrideOptions,
  lineItemHasPendingOverride,
  lineItemPriceHasChanged,
  setPriceOverrideDropdownOption,
  showCurrentPrice,
} from "@/components/StudentFulfillment/components/modals/utils.ts";
import BaseButton from "@/components/shared/Button/BaseButton.vue";
import CloseIcon from "@/components/svg-icons/CloseIcon.vue";
import { getOverrideReasons } from "@/composables/useOverrideReasons.js";
import {
  CANCELLATION_REASONS,
  CANCELLATION_REASONS_OTHER,
  ERROR_TIMEOUT,
  LIBRARY_IDS,
  MAX_NOTE_TEXTAREA_LIMIT,
  OPTIONS_PRICE_OVERRIDE,
  ORDER_PRODUCT_STATUS,
  ORDER_STATUS,
  SHARED_OVERRIDE_REASON_IDS,
  SPECIAL_EVENT_REASON_ID,
  SUCCESS_TIMEOUT,
} from "@/constants";
import { dollarsToCents } from "@/mixins/helpers";
import { useCreatePriceOverride } from "@/providers/priceOverrides/usePriceOverrideCreate";
import { getQueryKey as patchKey, usePatchPriceOverride } from "@/providers/priceOverrides/usePriceOverridePatch";
import { usePriceOverridesRevert } from "@/providers/priceOverrides/usePriceOverridesRevert";
import ordersService from "@/services/orders";
import CancelationReason from "./components/CancelSection.vue";
import KeepOrderPrice from "./components/KeepOrderSection.vue";
import PriceOverride from "./components/PriceOverrideSection.vue";
import { KEEP_ORDER_REASON_ERROR, THEADERS, VALIDATION_ERRORS } from "./constants";
import { modalHeaderPassthrough } from "./styles.js";
import type {
  ClassesFeeModalData,
  LineItemForModal,
  OrderUpdatePayload,
  PriceOverride as PriceOverrideType,
} from "./types";

defineOptions({
  name: "AcademicFeesModalUiV2",
});

const queryClient = useQueryClient();

const emit = defineEmits<{
  (e: "closeModal", order?: object): void;
  (e: "saveFulfillment"): void;
}>();

const props = defineProps({
  modalIsOpen: {
    type: Boolean,
    default: false,
  },
  orderId: {
    type: String,
    default: "",
  },
  orderStatus: {
    type: String,
    default: "",
  },
  lineItem: {
    type: Object as PropType<LineItemForModal>,
    default: () => ({}) as LineItemForModal,
  },
  statusOptions: {
    type: Array,
    default: () => [],
  },
  profileName: {
    type: String,
    default: "",
  },
  sessionPrices: {
    type: Object,
    default: () => {},
  },
});

const { orderId, orderStatus, modalIsOpen, lineItem, statusOptions, sessionPrices } = toRefs(props);
const isOpen = ref(modalIsOpen);

const { mutateAsync: createPriceOverrideRequest } = useCreatePriceOverride({
  onSuccess: (data, variables) => {
    toast.open({
      message: `Created Price Override for ${variables.name}`,
      type: "success",
      position: "bottom",
      duration: SUCCESS_TIMEOUT,
    });
  },
  onError: (error, variables) => {
    datadogLogs.logger.warn("Failed to create price override.", {
      error: error.message,
    });
    toast.open({
      message: `Couldn't create price override for ${variables.name}`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  },
});

const { mutateAsync: patchPriceOverrideRequest } = usePatchPriceOverride({
  onSuccess: async ({ id }, variables) => {
    await queryClient.invalidateQueries({ queryKey: patchKey(toRef(id)) });
    toast.open({
      message: `Updated Price Override for ${variables.name}`,
      type: "success",
      position: "bottom",
      duration: SUCCESS_TIMEOUT,
    });
  },
  onError: (error, variables) => {
    datadogLogs.logger.warn("Failed to update price override.", {
      error: error.message,
    });
    toast.open({
      message: `Couldn't update price override for ${variables.name}`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  },
});

const { mutateAsync: revertPriceOverrideRequest } = usePriceOverridesRevert({
  onSuccess: (data, variables) => {
    toast.open({
      message: `Reverted Price Override for ${variables.name}`,
      type: "success",
      position: "bottom",
      duration: SUCCESS_TIMEOUT,
    });
  },
  onError: (error) => {
    datadogLogs.logger.error("Error while creating price override", { data: error });
    toast.open({
      message: "Couldn't create price override for Classes Fee",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  },
});

const toast = useToast();
const { keepPriceReasons, customPriceReasons } = getOverrideReasons();

const state = reactive({
  isSaving: false,
  orderIsFulfilled: orderStatus.value === ORDER_STATUS.fulfilled,
});

const isCanceledSelected = computed(() => classesFeeModalData?.value?.status === ORDER_PRODUCT_STATUS.canceled);

const isFulfilledSelected = computed(() => classesFeeModalData?.value?.status === ORDER_PRODUCT_STATUS.fulfilled);

const usingPriceOverride = computed(
  () => classesFeeModalData?.value?.price_option === OPTIONS_PRICE_OVERRIDE.CUSTOM_PRICE.value,
);

const disablePriceDropdown = computed(
  () =>
    classesFeeModalData?.value?.pending_price_override ||
    classesFeeModalData?.value?.disable_price_dropdown ||
    state.isSaving ||
    state.orderIsFulfilled ||
    (!isFulfilledSelected.value && !isCanceledSelected.value),
);

const checkOtherCancellationReason = (cancellationReason?: string | null): boolean =>
  !!cancellationReason && CANCELLATION_REASONS.findIndex(({ id }) => id === cancellationReason) < 0;

const showCancellationReasonText = computed(
  () =>
    classesFeeModalData?.value?.cancellationReason === CANCELLATION_REASONS_OTHER ||
    checkOtherCancellationReason(classesFeeModalData?.value?.cancellationReason),
);

const showKeepOrderReasonSection = computed(
  () =>
    classesFeeModalData?.value?.price_option === OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value &&
    (isFulfilledSelected.value || isCanceledSelected.value) &&
    !shouldSkipPriceOverride.value,
);

const current_session_pricing = computed(() => {
  return lineItemPriceHasChanged(lineItem?.value, sessionPrices?.value?.additional_fees, LIBRARY_IDS.miscProducts);
});

const pending_price_override: ComputedRef = computed(() => {
  return lineItemHasPendingOverride(lineItem?.value) ? lineItem?.value.latest_unapproved_price_override : {};
});

const shouldSkipPriceOverride = computed(() => {
  return classesFeeModalData?.value?.price_option === OPTIONS_PRICE_OVERRIDE.KEEP_CURRENT.value ? true : undefined;
});

const classesFeeModalData = ref<ClassesFeeModalData>({
  id: lineItem.value?.id,
  status: lineItem.value?.status ?? ORDER_PRODUCT_STATUS.unfulfilled,
  cancellationReason: undefined,
  otherCancellationReasonText: undefined,

  price_override: generateDefaultPriceOverride(
    lineItem.value,
    setPriceOverrideDropdownOption(lineItem.value, current_session_pricing.value),
    pending_price_override.value,
    Libraries._4873ff1752624282A28cC8c40bf5fef6,
    orderId.value,
  ),

  current_session_pricing: current_session_pricing.value,
  current_price_config: showCurrentPrice(
    pending_price_override.value,
    current_session_pricing.value?.total_price_in_cents,
    lineItem.value?.total_price_in_cents,
  ),
  pending_price_override: !!(pending_price_override.value as { id?: string })?.id,
  price_options: generatePriceOverrideOptions(lineItem.value, current_session_pricing.value),
  price_option: setPriceOverrideDropdownOption(lineItem.value, current_session_pricing.value),
  price_override_status: buildStatusRow(lineItem.value),
  latest_approved_price_override: lineItem.value?.price_override ?? {},
  total_price_in_cents: lineItem.value?.total_price_in_cents,
  price_before_fulfillment_in_cents: lineItem.value?.price_before_fulfillment_in_cents,
});

const rules = {
  cancellationReason: {
    requiredIfCanceled: helpers.withMessage(VALIDATION_ERRORS.keepReason, (value, parentVm) =>
      parentVm?.status === "Canceled" ? !!value : true,
    ),
  },
  otherCancellationReasonText: {
    requiredIfCanceledAndOther: helpers.withMessage(
      VALIDATION_ERRORS.otherKeepReason,
      (value: string | undefined, parentVm?: ClassesFeeModalData) =>
        parentVm?.status === ORDER_PRODUCT_STATUS.canceled &&
        parentVm?.cancellationReason === CANCELLATION_REASONS_OTHER
          ? !!value
          : true,
    ),
    maxLength: maxLength(MAX_NOTE_TEXTAREA_LIMIT),
  },
  price_override: {
    override_reason_id: {
      requiredIfOverriding: helpers.withMessage(KEEP_ORDER_REASON_ERROR, (value: string | undefined) => {
        if (
          (classesFeeModalData.value.status === ORDER_PRODUCT_STATUS.fulfilled ||
            classesFeeModalData.value.status === ORDER_PRODUCT_STATUS.canceled) &&
          !shouldSkipPriceOverride.value
        ) {
          return !!value;
        }
        return true;
      }),
    },
  },
};

const validationState = computed(() => ({
  cancellationReason: classesFeeModalData.value.cancellationReason,
  otherCancellationReasonText: classesFeeModalData.value.otherCancellationReasonText,
  price_override: classesFeeModalData.value.price_override,
  status: classesFeeModalData.value.status,
}));

const v$ = useVuelidate(rules, validationState, {
  $registerAs: `FulfillmentAcademicsClassesFeeModal-${uuid.v4()}`,
  $lazy: true,
  $autoDirty: true,
});

const openErrorToast = (message?: string) => {
  toast.open({
    message: `${message || ERROR_MESSAGE}`,
    type: "error",
    position: "bottom",
    duration: ERROR_TIMEOUT,
  });
};

const canCreatePriceOverride = () => {
  const user_wants_to_create_price_override =
    !classesFeeModalData?.value?.price_override?.id &&
    !!classesFeeModalData?.value?.price_override?.override_reason_id;
  const reason_allows_creation = ![...SHARED_OVERRIDE_REASON_IDS, SPECIAL_EVENT_REASON_ID].includes(
    classesFeeModalData?.value?.price_override?.override_reason_id ?? "",
  );
  return (
    [user_wants_to_create_price_override, reason_allows_creation].every((validation) => validation === true) &&
    !shouldSkipPriceOverride.value
  );
};

const createPriceOverride = async (): Promise<PriceOverrideDB | undefined> => {
  if (!canCreatePriceOverride()) return;
  const priceOverride = classesFeeModalData.value.price_override;

  const createResponse = await createPriceOverrideRequest({
    payload: {
      request_notes: priceOverride.request_notes,
      override_amount_in_cents: dollarsToCents(priceOverride.override_amount_in_dollars),
      order_id: priceOverride.order_id ?? "",
      product_id: classesFeeModalData.value.id,
      override_reason_id: priceOverride.override_reason_id ?? "",
      library_id: Libraries._4873ff1752624282A28cC8c40bf5fef6,
    },
    name: "Classes Fee",
  });

  return createResponse;
};

const patchPriceOverride = async (line_item: PriceOverrideType): Promise<PriceOverrideDB> => {
  return await patchPriceOverrideRequest({
    id: toRef(line_item.id ?? ""),
    payload: {
      override_amount_in_cents: dollarsToCents(line_item.override_amount_in_dollars),
      override_reason_id: line_item.override_reason_id,
      request_notes: line_item.request_notes,
    },
    name: "Classes Fee",
  });
};

const executeSaveAndClose = async ({ payload }: { payload: OrderUpdatePayload }): Promise<void> => {
  try {
    // v$.value.$reset();
    const isValid = await v$.value.$validate();

    const itemProductId = lineItem.value?.id;

    if (isValid && itemProductId && orderId.value) {
      const response = await ordersService.updateOrderLineItem({
        orderId: orderId.value,
        productId: itemProductId,
        payload,
        skipErrorBus: true,
      });

      const orderData = response?.data;
      const productDetails = orderData?.data?.product_details;

      if (productDetails) {
        toast.open({
          message: "Academics: Classes updated Successfully",
          type: "success",
          position: "bottom",
          duration: SUCCESS_TIMEOUT,
        });

        if (!pending_price_override.value?.id) {
          await createPriceOverride();
        } else if (canUpdatePriceOverride(true, classesFeeModalData.value)) {
          await patchPriceOverride(classesFeeModalData.value.price_override);
        }

        queryClient.invalidateQueries({ queryKey: ["order"] });
        emit("closeModal", orderData);
      }
    } else if (!isValid) {
      openErrorToast(unref(v$.value.$errors?.[0]?.$message));
    }
  } catch (error) {
    datadogLogs.logger.error("Error while creating price override", { data: error });
    openErrorToast();
  }
};

const saveClassesFulfillmentOrder = async (): Promise<void> => {
  state.isSaving = true;
  try {
    const use_latest_product_price = findLatestPrice(classesFeeModalData.value);
    const payload: OrderUpdatePayload = {
      new_product_id: lineItem.value?.id,
      status: classesFeeModalData.value?.status,
      quantity: lineItem.value?.quantity,
      use_latest_product_price,
    };

    if (payload.status === ORDER_PRODUCT_STATUS.canceled) {
      payload.cancellation_reason = showCancellationReasonText.value
        ? classesFeeModalData.value?.otherCancellationReasonText
        : classesFeeModalData.value?.cancellationReason;
    }

    await executeSaveAndClose({ payload });
  } catch (error) {
    datadogLogs.logger.error("Error while saving classes fee fulfillment", { data: error });
    toast.open({
      message: ERROR_MESSAGE,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  } finally {
    state.isSaving = false;
  }
};

const resetPriceOverride = (resetPriceOption: boolean = false): void => {
  if (resetPriceOption) {
    classesFeeModalData.value.price_option = setPriceOverrideDropdownOption(
      classesFeeModalData.value,
      current_session_pricing.value,
    );
  }
  classesFeeModalData.value.price_override = generateDefaultPriceOverride(
    classesFeeModalData.value,
    classesFeeModalData.value.price_option,
    lineItemHasPendingOverride(lineItem.value) && lineItem.value.latest_unapproved_price_override
      ? lineItem.value.latest_unapproved_price_override
      : ({} as PriceOverrideType),
    Libraries._4873ff1752624282A28cC8c40bf5fef6,
    orderId.value,
  );
};

const revertPriceOverride = async (): Promise<GetOnePriceOverrideRevertResponse | undefined> => {
  if (lineItem.value?.latest_approved_price_override?.id) {
    const revertResponse = await revertPriceOverrideRequest({
      payload: {
        decision_notes: "User reverted to original price.",
        id: lineItem.value?.latest_approved_price_override?.id,
      },
      name: "Classes Fee",
    });
    return revertResponse;
  }
};

watch(isOpen, (isOpenNew: boolean) => {
  if (!isOpenNew) return;

  const reason = lineItem.value?.cancellation_reason;
  const cancellationReason = checkOtherCancellationReason(reason) ? CANCELLATION_REASONS_OTHER : reason;
  const override_status = buildStatusRow(lineItem.value);
  const default_price_option = setPriceOverrideDropdownOption(lineItem.value, current_session_pricing.value);

  classesFeeModalData.value = {
    id: lineItem.value?.id,
    status: lineItem.value?.status ?? ORDER_PRODUCT_STATUS.unfulfilled,
    cancellationReason,
    otherCancellationReasonText: reason ?? "",
    price_override: generateDefaultPriceOverride(
      lineItem.value,
      default_price_option,
      pending_price_override.value,
      Libraries._4873ff1752624282A28cC8c40bf5fef6,
      orderId.value,
    ),
    current_session_pricing: current_session_pricing.value,
    current_price_config: showCurrentPrice(
      Boolean(pending_price_override.value?.id),
      current_session_pricing.value?.total_price_in_cents,
      lineItem.value?.total_price_in_cents,
    ),
    pending_price_override: !!(pending_price_override.value as { id?: string })?.id,
    price_options: generatePriceOverrideOptions(lineItem.value, current_session_pricing.value),
    price_option: default_price_option,
    price_override_status: override_status,
    latest_approved_price_override: lineItem.value?.price_override ?? {},
  };
});

const disableKeepOrderReasonsDropdown = computed(() => {
  const isFulfilled = classesFeeModalData?.value?.status === ORDER_PRODUCT_STATUS.fulfilled;
  const hasPendingPriceOverride = classesFeeModalData?.value?.pending_price_override;

  return Boolean(state.isSaving || (isFulfilled && hasPendingPriceOverride));
});
</script>

<template>
  <Dialog v-model:visible="isOpen" modal :pt="modalHeaderPassthrough" dismissableMask @update:visible="emit('closeModal')">
    <!-- HEADER -->
    <template #header>
      <div class="font-semibold font-montserrat text-slate-700">
        {{ profileName ? `${profileName}'s ` : "" }}Classes Fee
      </div>
    </template>

    <!-- CLOSE ICON -->
    <template #closeicon>
      <CloseIcon size="18" stroke="#037f80" stroke-width="1.75" />
    </template>

    <!-- CONTENT -->
    <div class="w-full">
      <!-- TABLE HEADER (desktop only) -->
      <div class="hidden md:grid grid-cols-3 border-b border-gray-200 bg-gray-100 py-3">
        <div v-for="header in THEADERS" :key="header" class="font-montserrat text-sm font-semibold px-2">
          {{ header }}
        </div>
      </div>

      <!-- MAIN ROW -->
      <div class="grid grid-cols-1 md:grid-cols-3 border-b border-gray-200 mt-4">
        <div class="px-2">
          <span class="font-medium text-sm">Classes Fee</span>
        </div>

        <!-- Price dropdown -->
        <div class="px-2">
          <Dropdown
            v-model="classesFeeModalData.price_option"
            :disabled="disablePriceDropdown"
            :options="classesFeeModalData.price_options"
            optionLabel="label"
            optionValue="value"
            placeholder="Select price"
            class="w-full"
            optionDisabled="disabled"
            @change="resetPriceOverride()"
          />
        </div>

        <!-- Status dropdown -->
        <div class="px-2">
          <Dropdown
            v-model="classesFeeModalData.status"
            :options="statusOptions"
            class="w-full"
            :disabled="
              state.isSaving ||
              (classesFeeModalData.status === ORDER_PRODUCT_STATUS.fulfilled &&
                classesFeeModalData.pending_price_override) ||
                classesFeeModalData.disable_price_dropdown
            "
            @change="resetPriceOverride(true)"
          />
        </div>
        <!-- Additional content -->
        <div class="grid grid-cols-1 md:grid-cols-3 md:col-span-3">
          <!-- KEEP ORDER PRICE REASON -->
          <KeepOrderPrice
            v-if="showKeepOrderReasonSection"
            v-model="classesFeeModalData.price_override.override_reason_id"
            :current-session-pricing="classesFeeModalData.current_price_config"
            :reasons="keepPriceReasons.misc.value"
            :error="v$.price_override.override_reason_id.$error"
            :errorMessage="KEEP_ORDER_REASON_ERROR"
            :inputId="'keep-price-reason-' + classesFeeModalData.id"
            :disabled="disableKeepOrderReasonsDropdown"
          />

          <!-- CANCELLATION SECTION -->
          <CancelationReason
            v-if="isCanceledSelected"
            class="py-5 col-start-2 col-span-2 py-5"
            v-model:dropdown="classesFeeModalData.cancellationReason"
            v-model:textarea="classesFeeModalData.otherCancellationReasonText"
            :options="CANCELLATION_REASONS"
            :error="v$.cancellationReason.$error"
            :errorMessage="v$.cancellationReason?.$errors[0]?.$message"
            :inputId="'cancellation-reason-' + classesFeeModalData.id"
            :disabled="state.isSaving"
            :other-visible="showCancellationReasonText"
            :textarea-error="v$.otherCancellationReasonText.$error"
            :textarea-errorMessage="v$.otherCancellationReasonText?.$errors[0]?.$message"
          />
          <!-- OVERRIDE PRICE SECTION -->
          <PriceOverride
            v-if="usingPriceOverride"
            class="py-5 col-start-2 col-span-2 py-5"
            v-model:dropdown="classesFeeModalData.price_override.override_reason_id"
            v-model:textarea="classesFeeModalData.price_override.request_notes"
            v-model:newprice="classesFeeModalData.price_override.override_amount_in_dollars"
            :reasons="customPriceReasons.misc.value"
            :error="v$.cancellationReason.$error"
            :errorMessage="v$.cancellationReason?.$errors[0]?.$message"
            :inputId="'cancellation-reason-' + classesFeeModalData.id"
            :disabled="state.isSaving"
            :other-visible="showCancellationReasonText"
            :textarea-error="v$.otherCancellationReasonText.$error"
            :textarea-errorMessage="v$.otherCancellationReasonText?.$errors[0]?.$message"
            :modal-data="classesFeeModalData"
            @revert="revertPriceOverride()"
          />

          <!-- Override status-->
          <div
            class="py-5 col-start-2 col-span-2 py-5 flex flex-col md:flex-row items-center mb-6 gap-2 pl-2"
            v-if="classesFeeModalData.price_override_status.display"
          >
            <p class="text-sm font-semibold">Override Request Status:</p>
            <!-- Override Icon -->
            <span class="material-symbols-outlined" :class="classesFeeModalData.price_override_status.labelColor">
              {{ classesFeeModalData.price_override_status.icon }}
            </span>
            <p>{{ classesFeeModalData.price_override_status.labelStatus }}</p>
            <p v-if="classesFeeModalData?.latest_approved_price_override?.id">
              <span
                class="uppercase text-sm font-semibold text-teal-900 cursor-pointer"
                @click.prevent="revertPriceOverride"
              >
                | {{ classesFeeModalData.price_override_status.revertLabel }}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- ACTIONS -->
    <div class="flex justify-end gap-3 mt-5">
      <BaseButton outlined @click="emit('closeModal')" class="px-7">
        Cancel
      </BaseButton>

      <ButtonWithSpinner
        :prop-loading="state.isSaving"
        data-cy="save-order-room-button"
        variant="secondary"
        variant-type="normal"
        @click.prevent="saveClassesFulfillmentOrder"
        class="px-7 font-semibold"
      >
        Save
      </ButtonWithSpinner>
    </div>
  </Dialog>
</template>
